/**
 * MineralInventoryDialog — Shows the player's ore inventory with
 * color-coded rarity indicators, market values, and quick-sell actions.
 */

import Phaser from 'phaser';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import type { MMOMonetizationManager } from '../../managers/MMOMonetizationManager';
import type { PlayerMineral, MineralType, MarketSummary } from '../../shared/common/MonetizationTypes';
import { Colors, CSSColors, Fonts, FontSize, Radius, createDialogChrome, closeDialog } from './GameUITheme';
import { AuctionHouseDialog } from './AuctionHouseDialog';
import { isMobile } from '../../utils/ResponsiveHelper';
import { I18n } from '../../i18n/I18nService';

const PAD = 12;

// Unified rarity colors — matches Trade Hub
const RARITY_COLORS: Record<string, { hex: number; css: string; label: string }> = {
  common:    { hex: 0x888888, css: '#aaaaaa', label: 'Common' },
  uncommon:  { hex: 0x4488ff, css: '#4488ff', label: 'Uncommon' },
  rare:      { hex: 0xffcc00, css: '#ffcc00', label: 'Rare' },
  epic:      { hex: 0xcc66ff, css: '#cc66ff', label: 'Epic' },
  legendary: { hex: 0xff8800, css: '#ff8800', label: 'Legendary' },
};

const RARITY_ORDER: Record<string, number> = {
  legendary: 0, epic: 1, rare: 2, uncommon: 3, common: 4,
};

interface LocationBreakdown {
  locationType: string;
  locationId: string;
  locationName: string;
  minerals: { mineralTypeId: string; mineralName: string; displayName: string; amount: number; rarity: string; color: string }[];
}

type InventoryView = 'aggregate' | 'locations';

export class MineralInventoryDialog extends Phaser.GameObjects.Container {
  private mobile: boolean;
  private listW: number;
  private detailW: number;
  private monetization: MMOMonetizationManager;
  private minerals: PlayerMineral[] = [];
  private mineralTypes: Map<string, MineralType> = new Map();
  private marketSummary: Map<string, MarketSummary> = new Map();
  private selectedMineral: PlayerMineral | null = null;

  /* per-location data */
  private locationBreakdown: LocationBreakdown[] = [];
  private locationTotals: Record<string, number> = {};
  private inventoryView: InventoryView = 'aggregate';
  private selectedLocation: LocationBreakdown | null = null;

  private listContainer: Phaser.GameObjects.Container;
  private detailContainer: Phaser.GameObjects.Container;
  private statusText: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene) {
    const mobile = isMobile(scene);
    const dw = mobile ? 340 : 652;
    const dh = mobile ? 540 : 560;
    const { width, height } = scene.cameras.main;
    super(scene, width / 2 - dw / 2, height / 2 - dh / 2);
    this.mobile = mobile;
    const listW = mobile ? dw - PAD * 2 : 260;
    const DETAIL_X = listW + PAD * 2;
    this.listW = listW;
    this.detailW = mobile ? dw - PAD * 2 : dw - DETAIL_X - PAD;

    this.monetization = ServiceLocator.get<MMOMonetizationManager>(Services.MMO_MONETIZATION);

    createDialogChrome(scene, this, dw, dh, {
      title: I18n.t('dialog.ore_inventory'),
      onClose: () => this.close(),
    });

    // Left: mineral list
    this.listContainer = scene.add.container(PAD, 58);
    this.add(this.listContainer);

    // Right: detail panel (overlay on mobile, side-by-side on desktop)
    this.detailContainer = scene.add.container(mobile ? PAD : DETAIL_X, 58);
    if (mobile) this.detailContainer.setVisible(false);
    this.add(this.detailContainer);

    // Status text (bottom right, doesn't overlap Browse button)
    this.statusText = scene.add.text(dw - PAD, dh - 15, '', {
      fontSize: FontSize.xs, color: '#556677', fontFamily: Fonts.body,
    });
    this.statusText.setOrigin(1, 0.5);
    this.add(this.statusText);

    // Browse Trade Hub button (bottom left, full list width)
    const browseBtn = this.createActionBtn(PAD, dh - 46, listW, 32, I18n.t('ore.browse_trade'), 0x1a2a44, () => {
      new AuctionHouseDialog(scene, { tab: 'browse' });
    });
    this.add(browseBtn);

    this.setDepth(2000);
    scene.add.existing(this);

    this.loadData();
  }

  private close(): void {
    closeDialog(this.scene, this);
  }

  private async loadData(): Promise<void> {
    this.statusText.setText('Loading...');

    const [minerals, types, summary, breakdown] = await Promise.all([
      this.monetization.fetchMineralInventory(),
      this.monetization.fetchMineralTypes(),
      this.monetization.fetchMarketSummary(),
      this.monetization.fetchMineralInventoryBreakdown(),
    ]);

    // Build lookups
    this.mineralTypes.clear();
    for (const t of types) this.mineralTypes.set(t.id, t);

    this.marketSummary.clear();
    for (const s of summary) this.marketSummary.set(s.mineralTypeId, s);

    // Per-location data
    this.locationBreakdown = breakdown.locations;
    this.locationTotals = breakdown.totals;

    // Sort by rarity (legendary first, then epic, rare, uncommon, common)
    this.minerals = minerals.sort((a, b) => {
      const ra = RARITY_ORDER[a.rarity] ?? 5;
      const rb = RARITY_ORDER[b.rarity] ?? 5;
      if (ra !== rb) return ra - rb;
      return a.mineralName.localeCompare(b.mineralName);
    });

    this.statusText.setText('');

    // Auto-select first
    if (!this.selectedMineral && this.minerals.length > 0) {
      this.selectedMineral = this.minerals[0];
    }

    this.renderList();
    this.renderDetail();
  }

  // ============ Mineral List (left column) ============

  private renderList(): void {
    this.listContainer.removeAll(true);
    let y = 0;

    // View toggle tabs
    const tabW = Math.floor((this.listW - 6) / 2);
    const tabH = 26;

    const aggTab = this.createViewTab(0, y, tabW, tabH, I18n.t('ore.tab_all'), this.inventoryView === 'aggregate', () => {
      this.inventoryView = 'aggregate';
      this.selectedLocation = null;
      this.renderList();
      this.renderDetail();
    });
    this.listContainer.add(aggTab);

    const locTab = this.createViewTab(tabW + 6, y, tabW, tabH, I18n.t('ore.tab_by_location'), this.inventoryView === 'locations', () => {
      this.inventoryView = 'locations';
      this.renderList();
      this.renderDetail();
    });
    this.listContainer.add(locTab);
    y += tabH + 8;

    if (this.inventoryView === 'aggregate') {
      this.renderAggregateList(y);
    } else {
      this.renderLocationList(y);
    }
  }

  private renderAggregateList(startY: number): void {
    if (this.minerals.length === 0) {
      const empty = this.scene.add.text(this.listW / 2, startY + 40, 'No ores collected.\nMine asteroids to\ncollect minerals!', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body, align: 'center',
      });
      empty.setOrigin(0.5, 0);
      this.listContainer.add(empty);
      return;
    }

    let y = startY;
    for (const mineral of this.minerals) {
      const card = this.createMineralCard(mineral, y);
      this.listContainer.add(card);
      y += 52;
    }
  }

  private renderLocationList(startY: number): void {
    if (this.locationBreakdown.length === 0) {
      const empty = this.scene.add.text(this.listW / 2, startY + 40, 'No minerals stored\nat any location.', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body, align: 'center',
      });
      empty.setOrigin(0.5, 0);
      this.listContainer.add(empty);
      return;
    }

    let y = startY;
    for (const loc of this.locationBreakdown) {
      const isSelected = this.selectedLocation?.locationId === loc.locationId;
      const totalAtLoc = loc.minerals.reduce((sum, m) => sum + m.amount, 0);
      const CARD_H = 54;
      const card = this.scene.add.container(0, y);

      const bg = this.scene.add.graphics();
      const drawBg = (selected: boolean, hover: boolean) => {
        bg.clear();
        const fill = selected ? 0x253a66 : hover ? 0x1e2e55 : Colors.bgCard;
        bg.fillStyle(fill, 0.9);
        bg.fillRoundedRect(0, 0, this.listW, CARD_H, Radius.sm);
        bg.lineStyle(1, selected ? Colors.cyan : Colors.borderDim, selected ? 0.3 : hover ? 0.15 : 0.08);
        bg.strokeRoundedRect(0, 0, this.listW, CARD_H, Radius.sm);
      };
      drawBg(isSelected, false);
      card.add(bg);

      // Location icon + name
      const icon = loc.locationType === 'planet' ? '🌍' : '🛸';
      const locName = loc.locationName || loc.locationId;
      card.add(this.scene.add.text(10, 7, `${icon} ${locName}`, {
        fontSize: FontSize.sm, color: isSelected ? CSSColors.textWhite : '#aaccdd', fontFamily: Fonts.heading,
      }));

      // Mineral count summary
      const mineralSummary = loc.minerals.length === 1
        ? `${loc.minerals[0].displayName || loc.minerals[0].mineralName}`
        : `${loc.minerals.length} types`;
      card.add(this.scene.add.text(10, 27, mineralSummary, {
        fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
      }));

      // Total amount (right)
      card.add(this.scene.add.text(this.listW - 10, 7, `x${Math.floor(totalAtLoc).toLocaleString()}`, {
        fontSize: FontSize.sm, color: '#55cc88', fontFamily: Fonts.heading,
      }).setOrigin(1, 0));

      card.add(this.scene.add.text(this.listW - 10, 27, loc.locationType, {
        fontSize: '10px', color: '#556677', fontFamily: Fonts.body,
      }).setOrigin(1, 0));

      const zone = this.scene.add.zone(0, 0, this.listW, CARD_H).setOrigin(0, 0);
      zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.listW, CARD_H), Phaser.Geom.Rectangle.Contains);
      zone.on('pointerover', () => {
        if (!isSelected) drawBg(false, true);
        this.scene.input.setDefaultCursor('pointer');
      });
      zone.on('pointerout', () => {
        drawBg(isSelected, false);
        this.scene.input.setDefaultCursor('default');
      });
      zone.on('pointerdown', () => {
        this.selectedLocation = loc;
        this.renderList();
        this.renderDetail();
      });
      card.add(zone);

      this.listContainer.add(card);
      y += CARD_H + 4;
    }
  }

  private createViewTab(
    x: number, y: number, w: number, h: number,
    label: string, active: boolean, onClick: () => void,
  ): Phaser.GameObjects.Container {
    const tab = this.scene.add.container(x, y);
    const bg = this.scene.add.graphics();
    if (active) {
      bg.fillStyle(Colors.cyan, 1);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
    } else {
      bg.fillStyle(0x1e2844, 0.8);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, 0x334466, 0.3);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
    }
    tab.add(bg);

    tab.add(this.scene.add.text(w / 2, h / 2, label, {
      fontSize: FontSize.xs, color: active ? '#000000' : '#99aacc', fontFamily: Fonts.heading,
    }).setOrigin(0.5));

    const zone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    zone.on('pointerover', () => this.scene.input.setDefaultCursor('pointer'));
    zone.on('pointerout', () => this.scene.input.setDefaultCursor('default'));
    zone.on('pointerdown', onClick);
    tab.add(zone);

    return tab;
  }

  private createMineralCard(mineral: PlayerMineral, y: number): Phaser.GameObjects.Container {
    const isSelected = this.selectedMineral?.mineralTypeId === mineral.mineralTypeId;
    const card = this.scene.add.container(0, y);
    const cardH = 48;

    const rarityInfo = RARITY_COLORS[mineral.rarity] || RARITY_COLORS.common;
    const mineralHex = mineral.color ? parseInt(mineral.color.replace('#', ''), 16) : rarityInfo.hex;

    // Background
    const bg = this.scene.add.graphics();
    const drawBg = (selected: boolean, hover: boolean) => {
      bg.clear();
      const fill = selected ? 0x253a66 : hover ? 0x1e2e55 : Colors.bgCard;
      bg.fillStyle(fill, 0.9);
      bg.fillRoundedRect(0, 0, this.listW, cardH, Radius.sm);
      const bColor = selected ? Colors.cyan : hover ? Colors.cyan : Colors.borderDim;
      const bAlpha = selected ? 0.3 : hover ? 0.15 : 0.08;
      bg.lineStyle(1, bColor, bAlpha);
      bg.strokeRoundedRect(0, 0, this.listW, cardH, Radius.sm);
    };
    drawBg(isSelected, false);
    card.add(bg);

    // Mineral color circle
    const circle = this.scene.add.graphics();
    circle.fillStyle(mineralHex, 1);
    circle.fillCircle(20, cardH / 2, 10);
    circle.lineStyle(1, 0xffffff, 0.2);
    circle.strokeCircle(20, cardH / 2, 10);
    card.add(circle);

    // Name
    const nameText = this.scene.add.text(38, 8, mineral.displayName || mineral.mineralName, {
      fontSize: FontSize.sm,
      color: isSelected ? CSSColors.textWhite : rarityInfo.css,
      fontFamily: isSelected ? Fonts.heading : Fonts.body,
    });
    card.add(nameText);

    // Amount
    const amountText = this.scene.add.text(38, 26, `x${mineral.amount.toLocaleString()}`, {
      fontSize: FontSize.xs, color: '#889999', fontFamily: Fonts.body,
    });
    card.add(amountText);

    // Rarity label (right)
    const rarityLabel = this.scene.add.text(this.listW - 8, cardH / 2, rarityInfo.label, {
      fontSize: '10px', color: rarityInfo.css, fontFamily: Fonts.heading,
    });
    rarityLabel.setOrigin(1, 0.5);
    card.add(rarityLabel);

    // Interactive zone
    const hitZone = this.scene.add.zone(0, 0, this.listW, cardH).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.listW, cardH), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      if (!isSelected) drawBg(false, true);
      this.scene.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      drawBg(isSelected, false);
      this.scene.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', () => {
      this.selectedMineral = mineral;
      if (this.mobile) {
        this.listContainer.setVisible(false);
        this.detailContainer.setVisible(true);
      }
      this.renderList();
      this.renderDetail();
    });
    card.add(hitZone);

    return card;
  }

  // ============ Detail Panel (right column) ============

  private renderDetail(): void {
    this.detailContainer.removeAll(true);

    if (this.mobile) {
      const backBtn = this.scene.add.text(0, 0, '\u2190 Back', {
        fontSize: FontSize.sm, color: '#00ffff', fontFamily: Fonts.body,
      }).setInteractive().on('pointerdown', () => {
        this.detailContainer.setVisible(false);
        this.listContainer.setVisible(true);
      });
      this.detailContainer.add(backBtn);
    }

    if (this.inventoryView === 'locations') {
      this.renderLocationDetail();
      return;
    }

    if (!this.selectedMineral) {
      const hint = this.scene.add.text(this.detailW / 2, 60, 'Select an ore\nto view details', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body, align: 'center',
      });
      hint.setOrigin(0.5, 0);
      this.detailContainer.add(hint);
      return;
    }

    const mineral = this.selectedMineral;
    const rarityInfo = RARITY_COLORS[mineral.rarity] || RARITY_COLORS.common;
    const mineralHex = mineral.color ? parseInt(mineral.color.replace('#', ''), 16) : rarityInfo.hex;
    const typeInfo = this.mineralTypes.get(mineral.mineralTypeId);
    const summary = this.marketSummary.get(mineral.mineralTypeId);

    let y = 0;

    // Large mineral icon + name
    const bigCircle = this.scene.add.graphics();
    bigCircle.fillStyle(mineralHex, 1);
    bigCircle.fillCircle(22, 22, 18);
    bigCircle.lineStyle(2, 0xffffff, 0.25);
    bigCircle.strokeCircle(22, 22, 18);
    this.detailContainer.add(bigCircle);

    const titleText = this.scene.add.text(50, 4, mineral.displayName || mineral.mineralName, {
      fontSize: FontSize.xl, color: rarityInfo.css, fontFamily: Fonts.heading,
    });
    this.detailContainer.add(titleText);

    const rarityBadge = this.scene.add.text(50, 26, rarityInfo.label, {
      fontSize: FontSize.xs, color: rarityInfo.css, fontFamily: Fonts.heading,
    });
    this.detailContainer.add(rarityBadge);
    y += 50;

    // Description (if available from mineral type data)
    if (typeInfo?.description) {
      const desc = this.scene.add.text(0, y, typeInfo.description, {
        fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
        wordWrap: { width: this.detailW }, lineSpacing: 2,
      });
      this.detailContainer.add(desc);
      y += desc.height + 10;
    }

    // Divider
    this.addDivider(y); y += 10;

    // Inventory stats — primary count is larger
    const invLabel = this.scene.add.text(0, y, `${I18n.t('ore.in_inventory')}: `, {
      fontSize: FontSize.sm, color: '#889999', fontFamily: Fonts.body,
    });
    this.detailContainer.add(invLabel);

    const invVal = this.scene.add.text(invLabel.width, y, mineral.amount.toLocaleString(), {
      fontSize: FontSize.sm, color: CSSColors.textWhite, fontFamily: Fonts.heading,
    });
    this.detailContainer.add(invVal);
    y += 24;

    this.addInfoRow(0, y, I18n.t('ore.total_mined'), `${mineral.totalMined.toLocaleString()}`, '#889999');
    y += 22;

    // Base value
    if (typeInfo) {
      this.addInfoRow(0, y, I18n.t('ore.base_value'), `$${typeInfo.baseValueUsd.toFixed(2)} each`, '#889999');
      y += 22;
    }

    // Divider
    y += 4;
    this.addDivider(y); y += 10;

    // Market data section
    const marketHeader = this.scene.add.text(0, y, I18n.t('ore.market_data'), {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    });
    this.detailContainer.add(marketHeader);
    y += 18;

    if (summary && summary.totalListings > 0) {
      this.addInfoRow(0, y, I18n.t('ore.lowest'), `$${summary.lowestPrice.toFixed(2)}`, '#aaccdd');
      y += 20;

      this.addInfoRow(0, y, I18n.t('ore.average'), `$${summary.recentAvgPrice.toFixed(2)}`, CSSColors.gold);
      y += 20;

      this.addInfoRow(0, y, I18n.t('ore.highest'), `$${summary.highestPrice.toFixed(2)}`, '#aaccdd');
      y += 20;

      this.addInfoRow(0, y, I18n.t('ore.active_listings'), `${summary.totalListings}`, '#889999');
      y += 24;

      // Estimated total value
      const estPrice = summary.recentAvgPrice > 0 ? summary.recentAvgPrice : (typeInfo?.baseValueUsd ?? 0);
      const estValue = mineral.amount * estPrice;
      if (estValue > 0) {
        this.addInfoRow(0, y, 'Est. Total Value', `$${estValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, CSSColors.gold);
        y += 24;
      }
    } else {
      // No market data — use base value
      const noData = this.scene.add.text(0, y, I18n.t('ore.no_listings'), {
        fontSize: FontSize.xs, color: '#556677', fontFamily: Fonts.body,
      });
      this.detailContainer.add(noData);
      y += 18;

      if (typeInfo && typeInfo.baseValueUsd > 0) {
        const estValue = mineral.amount * typeInfo.baseValueUsd;
        this.addInfoRow(0, y, I18n.t('ore.est_value'), `$${estValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, '#889999');
        y += 24;
      } else {
        y += 10;
      }
    }

    // Sell button
    this.detailContainer.add(
      this.createActionBtn(0, y, this.detailW, 36, I18n.t('ore.sell'), 0x1a4422, () => {
        new AuctionHouseDialog(this.scene, { tab: 'sell', mineralTypeId: mineral.mineralTypeId });
      }),
    );
  }

  // ============ Location Detail ============

  private renderLocationDetail(): void {
    if (!this.selectedLocation) {
      const hint = this.scene.add.text(this.detailW / 2, 60, I18n.t('ore.select_location'), {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body, align: 'center',
      });
      hint.setOrigin(0.5, 0);
      this.detailContainer.add(hint);
      return;
    }

    const loc = this.selectedLocation;
    let y = 0;

    // Header
    const icon = loc.locationType === 'planet' ? '🌍' : '🛸';
    const locName = loc.locationName || loc.locationId;
    this.detailContainer.add(this.scene.add.text(0, y, `${icon} ${locName}`, {
      fontSize: FontSize.xl, color: CSSColors.textWhite, fontFamily: Fonts.heading,
    }));
    y += 24;

    this.detailContainer.add(this.scene.add.text(0, y, loc.locationType.charAt(0).toUpperCase() + loc.locationType.slice(1), {
      fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
    }));
    y += 20;

    const divG = this.scene.add.graphics();
    divG.lineStyle(1, Colors.cyan, 0.08);
    divG.lineBetween(0, y, this.detailW, y);
    this.detailContainer.add(divG);
    y += 10;

    // Total minerals at this location
    const totalAtLoc = loc.minerals.reduce((sum, m) => sum + m.amount, 0);
    this.addInfoRow(0, y, I18n.t('ore.total_minerals'), Math.floor(totalAtLoc).toLocaleString(), '#55cc88');
    y += 22;
    this.addInfoRow(0, y, I18n.t('ore.mineral_types'), `${loc.minerals.length}`, '#889999');
    y += 26;

    // Divider
    const divG2 = this.scene.add.graphics();
    divG2.lineStyle(1, Colors.cyan, 0.08);
    divG2.lineBetween(0, y, this.detailW, y);
    this.detailContainer.add(divG2);
    y += 10;

    // Mineral list for this location
    this.detailContainer.add(this.scene.add.text(0, y, I18n.t('ore.stored_minerals'), {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    }));
    y += 18;

    const RARITY_CSS: Record<string, string> = {
      common: '#aaaaaa', uncommon: '#4488ff', rare: '#ffcc00',
      epic: '#cc66ff', legendary: '#ff8800',
    };

    // Sort by rarity
    const sorted = [...loc.minerals].sort((a, b) => {
      const ra = RARITY_ORDER[a.rarity] ?? 5;
      const rb = RARITY_ORDER[b.rarity] ?? 5;
      return ra - rb;
    });

    for (const mineral of sorted) {
      const ROW_H = 24;
      const mineralHex = mineral.color ? parseInt(mineral.color.replace('#', ''), 16) : 0x888888;

      // Color dot
      const dot = this.scene.add.graphics();
      dot.fillStyle(mineralHex, 1);
      dot.fillCircle(6, y + ROW_H / 2, 4);
      this.detailContainer.add(dot);

      // Name
      this.detailContainer.add(this.scene.add.text(16, y + 2, mineral.displayName || mineral.mineralName, {
        fontSize: FontSize.xs, color: RARITY_CSS[mineral.rarity] || '#aaaaaa', fontFamily: Fonts.body,
      }));

      // Amount (right-aligned)
      this.detailContainer.add(this.scene.add.text(this.detailW, y + 2, `x${Math.floor(mineral.amount).toLocaleString()}`, {
        fontSize: FontSize.xs, color: '#aaccdd', fontFamily: Fonts.heading,
      }).setOrigin(1, 0));

      y += ROW_H;
    }

    y += 10;

    // Show what % of total this location holds
    const grandTotal = Object.values(this.locationTotals).reduce((s, v) => s + v, 0);
    if (grandTotal > 0) {
      const pct = ((totalAtLoc / grandTotal) * 100).toFixed(1);
      this.addInfoRow(0, y, 'Share of Total', `${pct}%`, '#889999');
    }
  }

  // ============ Helpers ============

  private addDivider(y: number): void {
    const g = this.scene.add.graphics();
    g.lineStyle(1, Colors.cyan, 0.08);
    g.lineBetween(0, y, this.detailW, y);
    this.detailContainer.add(g);
  }

  private addInfoRow(x: number, y: number, label: string, value: string, valueColor: string): void {
    const lbl = this.scene.add.text(x, y, `${label}: `, {
      fontSize: FontSize.xs, color: '#889999', fontFamily: Fonts.body,
    });
    this.detailContainer.add(lbl);

    const val = this.scene.add.text(x + lbl.width, y, value, {
      fontSize: FontSize.xs, color: valueColor, fontFamily: Fonts.body,
    });
    this.detailContainer.add(val);
  }

  private createActionBtn(
    x: number, y: number, w: number, h: number,
    label: string, color: number, callback: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    bg.fillStyle(color, 1);
    bg.fillRoundedRect(0, 0, w, h, Radius.sm);
    bg.lineStyle(1, Colors.cyan, 0.15);
    bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
    btn.add(bg);

    const text = this.scene.add.text(w / 2, h / 2, label, {
      fontSize: FontSize.sm, color: CSSColors.textWhite, fontFamily: Fonts.heading,
    });
    text.setOrigin(0.5);
    btn.add(text);

    const hitZone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(color + 0x111111, 1);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.3);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      this.scene.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(color, 1);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.15);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      this.scene.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', callback);
    btn.add(hitZone);

    return btn;
  }
}
